// src/pages/PrivacyPolicyPage.jsx
// Aspreo Privacy Policy — standalone public page at /privacy
// Also linked from: AspreoSite footer, PublicBookingPage footer, Facebook Developer verification

import React from 'react';

const FONT   = "'Inter','Segoe UI',sans-serif";
const INDIGO = '#2B1DC9';
const DARK   = '#0F0E1A';
const MUTED  = '#64748B';
const BORDER = '#E2E8F0';
const BG     = '#F8F9FF';

const EFFECTIVE_DATE = 'April 1, 2026';
const CONTACT_EMAIL  = 'privacy@aspreo.com';
const SUPPORT_EMAIL  = 'support@aspreo.com';
const SITE_URL       = 'https://www.aspreo.com';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: 20, fontWeight: 800, color: DARK, marginBottom: 14, paddingBottom: 10, borderBottom: `2px solid ${INDIGO}20` }}>
      {title}
    </h2>
    <div style={{ fontSize: 15, lineHeight: 1.85, color: '#374151' }}>
      {children}
    </div>
  </div>
);

const Li = ({ children }) => (
  <li style={{ marginBottom: 8, paddingLeft: 4 }}>{children}</li>
);

const Bold = ({ children }) => (
  <strong style={{ color: DARK, fontWeight: 700 }}>{children}</strong>
);

export default function PrivacyPolicyPage() {
  return (
    <div style={{ fontFamily: FONT, background: BG, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: DARK, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <a href={SITE_URL} style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
          <div style={{ width: 32, height: 32, background: INDIGO, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#fff' }}>A</span>
          </div>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-.02em' }}>Aspreo</span>
        </a>
        <span style={{ color: 'rgba(255,255,255,.3)', fontSize: 14, marginLeft: 4 }}>/ Privacy Policy</span>
      </div>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${INDIGO} 0%, #7C3AED 100%)`, padding: '56px 24px 48px', textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h1 style={{ fontSize: 36, fontWeight: 900, margin: '0 0 12px', letterSpacing: '-.02em' }}>Privacy Policy</h1>
        <p style={{ fontSize: 16, opacity: 0.85, maxWidth: 560, margin: '0 auto 16px' }}>
          We believe your data belongs to you. Here's exactly what we collect, how we use it, and how we protect it.
        </p>
        <div style={{ display: 'inline-block', background: 'rgba(255,255,255,.15)', borderRadius: 20, padding: '6px 18px', fontSize: 13 }}>
          Effective Date: {EFFECTIVE_DATE}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '52px 24px 80px' }}>

        {/* Quick summary */}
        <div style={{ background: '#EEF2FF', border: `1.5px solid ${INDIGO}30`, borderRadius: 14, padding: '20px 24px', marginBottom: 44 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: INDIGO, marginBottom: 10 }}>📋 Summary (Plain English)</div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.8, color: '#374151' }}>
            <Li>We collect only what's necessary to run the app</Li>
            <Li>We never sell your data to anyone — period</Li>
            <Li>We use Facebook / Meta Login only for WhatsApp Business integration, not for advertising</Li>
            <Li>You can delete your account and all data at any time</Li>
            <Li>We use Supabase (Postgres) for storage with AES-256 encryption at rest</Li>
          </ul>
        </div>

        <Section title="1. Who We Are">
          <p>
            Aspreo ("<Bold>we</Bold>", "<Bold>us</Bold>", or "<Bold>our</Bold>") is a software-as-a-service platform
            that provides a unified life and business operating system for solopreneurs, freelancers, and small agencies.
            Aspreo is operated by Aspreo and accessible at <a href={SITE_URL} style={{ color: INDIGO }}>{SITE_URL}</a>.
          </p>
          <p style={{ marginTop: 12 }}>
            For privacy-related questions, contact us at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: INDIGO, fontWeight: 600 }}>{CONTACT_EMAIL}</a>
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p style={{ marginBottom: 14 }}><Bold>2.1 Information you provide directly</Bold></p>
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            <Li><Bold>Account information:</Bold> Name, email address, password (stored as a bcrypt hash — never plain text)</Li>
            <Li><Bold>Profile information:</Bold> Business name, logo, address, GST number, PAN, bank details (used solely to generate your invoices and proposals)</Li>
            <Li><Bold>App content:</Bold> Goals, tasks, habits, finances, journal entries, ideas, vision board content, projects, contacts, invoices, and any other data you enter into the app</Li>
            <Li><Bold>Booking information:</Bold> Your booking page username, availability settings, meeting types you create</Li>
            <Li><Bold>Payment settings:</Bold> Razorpay API key and secret (stored encrypted; we never access your Razorpay account — payments go directly to you)</Li>
          </ul>

          <p style={{ marginBottom: 14 }}><Bold>2.2 Information collected automatically</Bold></p>
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            <Li><Bold>Usage data:</Bold> Pages visited within the app, features used, session duration — collected anonymously to improve the product</Li>
            <Li><Bold>Device information:</Bold> Browser type, operating system, screen resolution</Li>
            <Li><Bold>Log data:</Bold> IP address, timestamps of requests (retained for 30 days for security purposes only)</Li>
          </ul>

          <p style={{ marginBottom: 14 }}><Bold>2.3 Information from third-party integrations (when you connect them)</Bold></p>
          <ul style={{ paddingLeft: 20 }}>
            <Li><Bold>Google Calendar:</Bold> OAuth refresh token stored securely to create calendar events on your behalf. We do not read your existing calendar data.</Li>
            <Li><Bold>Google Sheets:</Bold> Sheet content you choose to sync (leads/clients data). We only read sheets you explicitly link.</Li>
            <Li><Bold>WhatsApp Business (via Meta / Facebook):</Bold> Your WhatsApp Business phone number, WABA ID, and access token — used only to send messages you initiate from Aspreo. See Section 6 for full details.</Li>
            <Li><Bold>Zoom / Twilio / Resend:</Bold> API credentials you provide — stored encrypted and used only to perform actions you trigger (send messages, emails, generate meeting links)</Li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <p style={{ marginBottom: 14 }}>We use your information to:</p>
          <ul style={{ paddingLeft: 20 }}>
            <Li>Provide, operate, and maintain the Aspreo platform</Li>
            <Li>Authenticate your identity and keep your account secure</Li>
            <Li>Generate invoices, proposals, and booking confirmations on your behalf</Li>
            <Li>Sync data with third-party services you have explicitly connected</Li>
            <Li>Send transactional emails (booking confirmations, invoice deliveries) — never marketing without consent</Li>
            <Li>Detect and prevent fraud, abuse, or security incidents</Li>
            <Li>Comply with legal obligations</Li>
            <Li>Improve the product based on aggregated, anonymised usage patterns</Li>
          </ul>
          <p style={{ marginTop: 16, fontWeight: 700, color: '#DC2626' }}>
            We do NOT use your data for advertising. We do NOT sell your data. We do NOT share your data with third parties for their marketing purposes.
          </p>
        </Section>

        <Section title="4. Legal Basis for Processing (GDPR)">
          <p style={{ marginBottom: 12 }}>If you are in the European Economic Area, we process your data under the following legal bases:</p>
          <ul style={{ paddingLeft: 20 }}>
            <Li><Bold>Contract performance:</Bold> Processing necessary to provide the service you signed up for</Li>
            <Li><Bold>Legitimate interests:</Bold> Security monitoring, fraud prevention, product improvement</Li>
            <Li><Bold>Consent:</Bold> Third-party integrations (Google, Meta/WhatsApp, Zoom) — you explicitly connect these and can disconnect at any time</Li>
            <Li><Bold>Legal obligation:</Bold> Compliance with applicable laws</Li>
          </ul>
        </Section>

        <Section title="5. Data Sharing and Disclosure">
          <p style={{ marginBottom: 14 }}>We share your data only in these limited circumstances:</p>
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            <Li>
              <Bold>Service providers:</Bold> Supabase (database and authentication), Vercel (hosting), Resend (transactional email),
              Razorpay (payment processing — your data goes directly to your Razorpay account, not ours).
              These providers process data under strict data processing agreements.
            </Li>
            <Li><Bold>Legal requirements:</Bold> If required by law, court order, or government authority</Li>
            <Li><Bold>Business transfer:</Bold> If Aspreo is acquired or merges, your data would transfer to the new entity under the same privacy commitments</Li>
            <Li><Bold>Your direction:</Bold> When you share an invoice link or booking page — only the specific data you choose to share is accessible</Li>
          </ul>
          <p><Bold>We never share your data with advertisers, data brokers, or social media platforms for targeting purposes.</Bold></p>
        </Section>

        <Section title="6. Facebook / Meta Platform and WhatsApp Integration">
          <p style={{ marginBottom: 14 }}>
            Aspreo integrates with the <Bold>Meta (Facebook) platform</Bold> solely to provide WhatsApp Business messaging functionality.
            This section explains exactly how we use Meta's platform and what data we access.
          </p>

          <p style={{ marginBottom: 10 }}><Bold>6.1 What we access from Meta</Bold></p>
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            <Li>Your <Bold>WhatsApp Business Account (WABA) ID</Bold> — used to identify your connected WhatsApp account</Li>
            <Li>Your <Bold>WhatsApp phone number and phone number ID</Bold> — used to send messages on your behalf</Li>
            <Li>A <Bold>WhatsApp Cloud API access token</Bold> — stored encrypted in our database to authenticate message-sending requests</Li>
          </ul>

          <p style={{ marginBottom: 10 }}><Bold>6.2 What we do NOT access from Meta</Bold></p>
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            <Li>We do <Bold>not</Bold> access your personal Facebook profile or friends list</Li>
            <Li>We do <Bold>not</Bold> access your Facebook Pages, Instagram, or any other Meta product</Li>
            <Li>We do <Bold>not</Bold> read incoming WhatsApp messages unless you explicitly set up a webhook</Li>
            <Li>We do <Bold>not</Bold> use Meta data for advertising or analytics</Li>
          </ul>

          <p style={{ marginBottom: 10 }}><Bold>6.3 How we use Meta data</Bold></p>
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            <Li>To send WhatsApp messages to phone numbers <Bold>you specify</Bold> from within your Aspreo dashboard</Li>
            <Li>To deliver invoice and booking notifications to your clients via WhatsApp, <Bold>only when you initiate the action</Bold></Li>
          </ul>

          <p style={{ marginBottom: 10 }}><Bold>6.4 Disconnecting Meta / WhatsApp</Bold></p>
          <p>
            You can disconnect your WhatsApp Business integration at any time from <Bold>Business → WhatsApp</Bold> in your Aspreo dashboard.
            Upon disconnection, we delete your stored access token and WABA credentials immediately.
            You can also revoke access from your Meta Business Suite at any time.
          </p>

          <p style={{ marginTop: 16 }}>
            <Bold>Meta's Privacy Policy:</Bold>{' '}
            <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noreferrer" style={{ color: INDIGO }}>
              https://www.facebook.com/privacy/policy/
            </a>
          </p>
        </Section>

        <Section title="7. Data Storage and Security">
          <ul style={{ paddingLeft: 20 }}>
            <Li><Bold>Storage:</Bold> All data is stored in Supabase (PostgreSQL) hosted on AWS infrastructure in the ap-south-1 (Mumbai) region</Li>
            <Li><Bold>Encryption at rest:</Bold> AES-256 encryption for all stored data</Li>
            <Li><Bold>Encryption in transit:</Bold> TLS 1.2+ for all data in transit</Li>
            <Li><Bold>Sensitive fields:</Bold> Vault passwords, API keys, and tokens are encrypted at the application layer before database storage — even Aspreo team members cannot read them</Li>
            <Li><Bold>Authentication:</Bold> Passwords stored as bcrypt hashes. Optional two-factor authentication (TOTP) available</Li>
            <Li><Bold>Access control:</Bold> Row Level Security (RLS) enforced at the database level — every query is filtered to the authenticated user's own data</Li>
            <Li><Bold>Security monitoring:</Bold> Failed login attempts, suspicious activity, and API anomalies are monitored and alerted</Li>
          </ul>
          <p style={{ marginTop: 16 }}>
            Despite these measures, no system is 100% secure. If you discover a security vulnerability, please report it to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: INDIGO }}>{CONTACT_EMAIL}</a> and we will respond within 48 hours.
          </p>
        </Section>

        <Section title="8. Data Retention">
          <ul style={{ paddingLeft: 20 }}>
            <Li><Bold>Active accounts:</Bold> Data is retained as long as your account is active</Li>
            <Li><Bold>After account deletion:</Bold> All personal data is permanently deleted within 30 days. Anonymised aggregated statistics may be retained.</Li>
            <Li><Bold>Backups:</Bold> Encrypted backups are retained for up to 7 days for disaster recovery, then permanently purged</Li>
            <Li><Bold>Log data:</Bold> Server access logs retained for 30 days, then deleted</Li>
            <Li><Bold>Payment records:</Bold> Razorpay transaction records are retained as required by Indian tax law (typically 7 years) — these are stored by Razorpay, not Aspreo</Li>
          </ul>
        </Section>

        <Section title="9. Your Rights">
          <p style={{ marginBottom: 14 }}>You have the following rights regarding your personal data:</p>
          <ul style={{ paddingLeft: 20 }}>
            <Li><Bold>Access:</Bold> Export all your data at any time from Settings → Export Backup</Li>
            <Li><Bold>Correction:</Bold> Edit your profile and any app data at any time within the app</Li>
            <Li><Bold>Deletion:</Bold> Delete your account from Settings → Delete Account. All data is permanently removed within 30 days.</Li>
            <Li><Bold>Portability:</Bold> Download a full JSON export of all your data from Settings → Export Backup</Li>
            <Li><Bold>Restriction:</Bold> Contact us to restrict specific processing activities</Li>
            <Li><Bold>Objection:</Bold> Contact us to object to processing based on legitimate interests</Li>
            <Li><Bold>Withdraw consent:</Bold> Disconnect any integration at any time — WhatsApp, Google Calendar, Google Sheets, Zoom</Li>
          </ul>
          <p style={{ marginTop: 16 }}>
            To exercise any right, email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: INDIGO }}>{CONTACT_EMAIL}</a>.
            We respond within 30 days. For EU residents, you may also lodge a complaint with your local supervisory authority.
          </p>
        </Section>

        <Section title="10. Cookies and Tracking">
          <ul style={{ paddingLeft: 20 }}>
            <Li><Bold>Session cookies:</Bold> Used for authentication — necessary for the app to function</Li>
            <Li><Bold>Preference cookies:</Bold> Store your theme preference (light/dark mode) and UI settings</Li>
            <Li><Bold>No advertising cookies:</Bold> We do not use Google Analytics, Facebook Pixel, or any advertising tracking</Li>
            <Li><Bold>No third-party tracking:</Bold> We do not load any third-party tracking scripts</Li>
          </ul>
        </Section>

        <Section title="11. Children's Privacy">
          <p>
            Aspreo is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13.
            If you believe a child under 13 has provided us with personal data, please contact{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: INDIGO }}>{CONTACT_EMAIL}</a> and we will delete it immediately.
          </p>
        </Section>

        <Section title="12. International Data Transfers">
          <p>
            Aspreo is operated from India and our primary data infrastructure is in AWS Mumbai (ap-south-1).
            If you access Aspreo from outside India, your data may be transferred to and processed in India.
            We ensure appropriate safeguards are in place for any international transfers in accordance with applicable data protection laws.
          </p>
        </Section>

        <Section title="13. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we make material changes, we will:
          </p>
          <ul style={{ paddingLeft: 20, marginTop: 10 }}>
            <Li>Update the "Effective Date" at the top of this page</Li>
            <Li>Send an email notification to all registered users</Li>
            <Li>Show an in-app notification on your next login</Li>
          </ul>
          <p style={{ marginTop: 12 }}>
            Continued use of Aspreo after changes take effect constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="14. Contact Us">
          <p style={{ marginBottom: 12 }}>For any privacy-related questions, requests, or concerns:</p>
          <div style={{ background: '#F0F0F8', borderRadius: 10, padding: '18px 22px', display: 'inline-block', minWidth: 320 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: DARK, marginBottom: 10 }}>Aspreo Privacy Team</div>
            <div style={{ fontSize: 14, color: MUTED, lineHeight: 2 }}>
              <div>📧 Privacy: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: INDIGO, fontWeight: 600 }}>{CONTACT_EMAIL}</a></div>
              <div>📧 Support: <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: INDIGO }}>{SUPPORT_EMAIL}</a></div>
              <div>🌐 Website: <a href={SITE_URL} style={{ color: INDIGO }}>{SITE_URL}</a></div>
              <div>⏱ Response time: Within 30 days (typically 2–3 business days)</div>
            </div>
          </div>
        </Section>

        {/* Bottom note */}
        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 28, marginTop: 12, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, fontSize: 13, color: MUTED }}>
          <span>© 2026 Aspreo. All rights reserved.</span>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="/terms" style={{ color: MUTED, textDecoration: 'none' }}>Terms of Service</a>
            <a href="/privacy" style={{ color: INDIGO, fontWeight: 700, textDecoration: 'none' }}>Privacy Policy</a>
            <a href="mailto:support@aspreo.com" style={{ color: MUTED, textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
}
